import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import cinesisLogo from '../assets/cinesis-logo.png';

const SignAgreementModal = lazy(() => import('../components/SignAgreementModal'));

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function DocumentUploadPage() {
  const { token } = useParams();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [signingDoc, setSigningDoc] = useState(null);
  const fileInputRefs = useRef({});

  const fetchLinkInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get(`${API_URL}/upload-links/link/${token}`);
      setLinkData(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setError('This upload link is not valid.');
      else if (status === 410) setError('This upload link has expired. Please contact your dispatcher for a new link.');
      else setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLinkInfo(); }, [fetchLinkInfo]);

  const handleFileSelect = async (docId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = '';

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setUploadErrors(prev => ({ ...prev, [docId]: 'Please upload a PDF or image (JPEG, PNG).' }));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadErrors(prev => ({ ...prev, [docId]: 'File must be under 5 MB.' }));
      return;
    }

    setUploadErrors(prev => ({ ...prev, [docId]: null }));
    setUploading(prev => ({ ...prev, [docId]: true }));

    try {
      const formData = new FormData();
      formData.append('document_type', docId);
      formData.append('file', file);

      const { data } = await axios.post(
        `${API_URL}/upload-links/link/${token}/submit`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setLinkData(prev => ({
        ...prev,
        status: data.status,
        documents: prev.documents.map(d => d.id === docId ? { ...d, submitted: true } : d),
      }));
    } catch (err) {
      setUploadErrors(prev => ({
        ...prev,
        [docId]: err.response?.data?.detail || 'Upload failed. Please try again.',
      }));
    } finally {
      setUploading(prev => ({ ...prev, [docId]: false }));
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <Page>
        <Card>
          <Header />
          <div className="text-center py-10">
            <span className="inline-block w-6 h-6 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="mt-4 text-sm text-slate-500">Loading...</p>
          </div>
        </Card>
      </Page>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <Page>
        <Card>
          <Header />
          <div className="text-center py-8">
            <span className="inline-block px-3.5 py-1 mb-3 text-[13px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full">
              Link Unavailable
            </span>
            <p className="max-w-xs mx-auto text-[15px] text-slate-500 leading-relaxed">{error}</p>
          </div>
        </Card>
      </Page>
    );
  }

  const allDone = linkData.status === 'complete';
  const done = linkData.documents.filter(d => d.submitted).length;
  const total = linkData.documents.length;

  /* ── All done ── */
  if (allDone) {
    return (
      <Page>
        <Card>
          <Header />
          <div className="text-center px-2 pt-8 pb-4">
            <div className="w-[72px] h-[72px] mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <polyline points="6 12.5 10.5 17 18 8" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="text-[22px] font-[650] tracking-tight text-slate-900 mb-3">All documents received</h2>
            <p className="max-w-[340px] mx-auto text-[15px] text-slate-600 leading-relaxed mb-1.5">
              Thank you, <strong className="font-semibold text-slate-900">{linkData.trucker_name}</strong>.
            </p>
            <p className="max-w-[340px] mx-auto text-[15px] text-slate-600 leading-relaxed">
              We&rsquo;ll reach out to you shortly regarding the next steps.
            </p>
          </div>
        </Card>
        <Footer />
      </Page>
    );
  }

  /* ── Upload form ── */
  return (
    <Page>
      <Card>
        <Header />

        <h2 className="text-[22px] font-[650] tracking-tight text-slate-900 mb-2.5">Carrier Document Upload</h2>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-2">
          Hi <strong className="font-semibold text-slate-900">{linkData.trucker_name}</strong>, please upload the required documents below to complete your carrier setup.
        </p>
        {linkData.mc_number && (
          <span className="inline-block mt-1 mb-1 px-2.5 py-0.5 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded">
            MC# {linkData.mc_number}
          </span>
        )}

        {/* progress */}
        <div className="my-5">
          <div className="flex justify-between text-xs font-medium text-slate-400 mb-1.5">
            <span>{done} of {total} uploaded</span>
            <span>{Math.round((done / total) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-600 rounded-full transition-[width] duration-300 ease-out"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
        </div>

        {/* docs */}
        <div className="flex flex-col gap-2 mb-5">
          {linkData.documents.map(doc => (
            doc.kind === 'sign' ? (
              <SignRow
                key={doc.id}
                doc={doc}
                onSign={() => setSigningDoc(doc)}
              />
            ) : (
              <DocRow
                key={doc.id}
                doc={doc}
                uploading={uploading[doc.id]}
                error={uploadErrors[doc.id]}
                inputRef={el => (fileInputRefs.current[doc.id] = el)}
                onPick={() => fileInputRefs.current[doc.id]?.click()}
                onFile={e => handleFileSelect(doc.id, e)}
              />
            )
          ))}
        </div>

        <div className="flex items-center justify-center gap-1.5 pt-4 border-t border-slate-100 text-xs text-slate-400">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#16a34a" strokeWidth="2" strokeLinejoin="round" />
          </svg>
          Your documents are securely handled
        </div>
      </Card>
      <Footer />

      {signingDoc && (
        <Suspense fallback={null}>
          <SignAgreementModal
            open
            onClose={() => setSigningDoc(null)}
            token={token}
            templateSlug={signingDoc.template_slug}
            mcNumber={linkData.mc_number}
            truckerName={linkData.trucker_name}
            onSigned={(resp) => {
              setLinkData(prev => ({
                ...prev,
                status: resp.status,
                documents: prev.documents.map(d =>
                  d.id === signingDoc.id ? { ...d, submitted: true } : d
                ),
              }));
            }}
          />
        </Suspense>
      )}
    </Page>
  );
}

/* ── Subcomponents ── */

function Page({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 pt-10 pb-8 font-sans text-slate-800">
      {children}
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="w-full max-w-[500px] bg-white border border-slate-200 rounded-xl p-8 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_4px_16px_rgba(0,0,0,0.04)]">
      {children}
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-center gap-2.5 mb-7 pb-5 border-b border-slate-100">
      <div
        aria-label="Cinesis"
        className="w-[30px] h-[30px]"
        style={{
          background: 'linear-gradient(135deg, #2563eb, #60a5fa)',
          mask: `url(${cinesisLogo}) center/contain no-repeat`,
          WebkitMask: `url(${cinesisLogo}) center/contain no-repeat`,
        }}
      />
      <span className="text-[17px] font-bold tracking-tight text-slate-900">Cinesis</span>
    </div>
  );
}

function Footer() {
  return (
    <div className="mt-4 text-center text-xs text-slate-400">
      Cinesis Freight Technologies, Inc.
    </div>
  );
}

function SignRow({ doc, onSign }) {
  const submitted = doc.submitted;
  return (
    <div className={`flex items-center justify-between gap-3 p-3.5 px-4 border rounded-[10px] transition-colors ${
      submitted ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="shrink-0">
          {submitted ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="11" fill="#dcfce7" stroke="#16a34a" strokeWidth="2" />
              <polyline points="7.5 12.5 10.5 15.5 16.5 9.5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M4 17l5-5 4 4 7-7" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 7h6v6" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800">{doc.label}</div>
          <div className={`text-xs mt-px ${submitted ? 'text-green-600 font-medium' : 'text-slate-400'}`}>
            {submitted ? 'Signed' : 'Sign in-app — no upload needed'}
          </div>
        </div>
      </div>
      {!submitted && (
        <button
          onClick={onSign}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"
        >
          Sign
        </button>
      )}
    </div>
  );
}

function DocRow({ doc, uploading, error, inputRef, onPick, onFile }) {
  const submitted = doc.submitted;

  return (
    <div className={`flex items-center justify-between gap-3 p-3.5 px-4 border rounded-[10px] transition-colors ${
      submitted
        ? 'bg-green-50 border-green-200'
        : 'bg-slate-50 border-slate-200'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="shrink-0">
          {submitted ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="11" fill="#dcfce7" stroke="#16a34a" strokeWidth="2" />
              <polyline points="7.5 12.5 10.5 15.5 16.5 9.5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="2" width="16" height="20" rx="2" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="9" y1="8" x2="15" y2="8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="9" y1="12" x2="13" y2="12" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800">{doc.label}</div>
          <div className={`text-xs mt-px ${submitted ? 'text-green-600 font-medium' : 'text-slate-400'}`}>
            {submitted ? 'Received' : 'PDF or image, max 5 MB'}
          </div>
        </div>
      </div>
      {!submitted && (
        <div className="shrink-0 flex flex-col items-end">
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic"
            className="hidden"
            onChange={onFile}
          />
          <button
            onClick={onPick}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors whitespace-nowrap"
          >
            {uploading ? (
              <>
                <span className="inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </button>
          {error && <div className="text-xs text-red-400 mt-1.5 text-right">{error}</div>}
        </div>
      )}
    </div>
  );
}
